import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import { Loader2, Instagram, Sparkles, Hash, AtSign, Star, Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Admin-only modal trigger that kicks off an Apify Instagram scrape.
 *
 * Sources can be a mix of:
 *   • profile handles  — `@igotskates`, `@rollerdiscolondon`
 *   • global hashtags  — `#rollerdisco`, `#skatemeet`
 * Accepts comma-separated and/or newline-separated input.
 *
 * On open, the textarea is pre-filled with the admin's SAVED auto-scrape
 * sources from `/admin/instagram/auto-scrape/settings` — the same list
 * the cron job runs against. That way "press Scrape" never asks the
 * admin to retype the list. They can still edit before running, and a
 * "Save to pinned list" button persists any edits back to the settings
 * doc so the next manual run + the next auto cron both pick them up.
 *
 * Fallback chain when the saved list is empty: try trusted-sources
 * (handles that have produced approved events) → fall back to a single
 * "@igotskates" seed so the box is never blank.
 */
const ScrapeInstagramButton = ({ variant = 'default', size = 'default', className = '' }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [sources, setSources] = useState('@igotskates');
  const [limit, setLimit] = useState(5);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Track where the prefill came from so we can show the right badge.
  const [pinnedCount, setPinnedCount] = useState(0);
  const [pinnedSnapshot, setPinnedSnapshot] = useState('');

  // Fetch saved sources whenever the modal opens. Done on-open (not
  // mount) so the list reflects every save the admin made since the
  // last time they opened it.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      const auth = { headers: { Authorization: `Bearer ${token}` } };
      try {
        // 1) Primary: the admin's PINNED auto-scrape list (single source
        //    of truth — what the cron runs against and what they edit
        //    in /admin/scrape-settings).
        const { data } = await axios.get(`${API}/admin/instagram/auto-scrape/settings`, auth);
        if (cancelled) return;
        // The API returns the array under `sources` (not `auto_scrape_sources`).
        const list = Array.isArray(data?.sources) ? data.sources : [];
        if (list.length > 0) {
          const joined = list.join('\n');
          setSources(joined);
          setPinnedSnapshot(joined);
          setPinnedCount(list.length);
          return;
        }
      } catch { /* fall through to trusted */ }
      try {
        // 2) Fallback: trusted sources (auto-learned from past approvals).
        const { data } = await axios.get(`${API}/admin/instagram/trusted-sources`, auth);
        if (cancelled) return;
        const list = (data?.items || []).map((r) => r.source).filter(Boolean);
        if (list.length > 0) {
          setSources(list.join('\n'));
          setPinnedSnapshot('');
          setPinnedCount(0);
        }
      } catch { /* keep the placeholder */ }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Tokenise the textarea — handles commas, newlines, and stray whitespace
  const parseSources = (raw) =>
    (raw || '')
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean);

  const parsed = parseSources(sources);
  const handleCount = parsed.filter((s) => !s.startsWith('#')).length;
  const hashtagCount = parsed.filter((s) => s.startsWith('#')).length;
  const isDirtyVsPinned = pinnedSnapshot && sources.trim() !== pinnedSnapshot.trim();

  // Persist the current textarea contents back to the auto-scrape
  // settings doc. After save, the next cron run + the next manual open
  // both see this list. We deduplicate (case-insensitive) before saving
  // so a sloppy paste doesn't leave the pinned list with repeats.
  const saveToPinnedList = async () => {
    const list = parseSources(sources);
    if (list.length === 0) {
      toast.error('Add at least one @handle or #hashtag before saving');
      return;
    }
    const seen = new Set();
    const deduped = [];
    for (const s of list) {
      const k = s.toLowerCase();
      if (!seen.has(k)) { seen.add(k); deduped.push(s); }
    }
    setSaving(true);
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      await axios.put(
        `${API}/admin/instagram/auto-scrape/settings`,
        { sources: deduped },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const joined = deduped.join('\n');
      setSources(joined);
      setPinnedSnapshot(joined);
      setPinnedCount(deduped.length);
      toast.success(`Saved ${deduped.length} sources to your pinned list`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save — try again');
    } finally {
      setSaving(false);
    }
  };

  const runScrape = async () => {
    const list = parseSources(sources);
    if (list.length === 0) {
      toast.error('Add at least one @handle or #hashtag');
      return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('rollm8s_token') || localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/admin/instagram/scrape`,
        { sources: list, results_limit: limit },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 },
      );
      toast.success(
        data?.message || `Scrape started — events will appear in your queue shortly.`,
      );
      setOpen(false);
      navigate('/admin/pending-events');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Scrape failed — try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant={variant}
          size={size}
          className={className}
          data-testid="scrape-instagram-trigger"
        >
          <Instagram className="w-4 h-4 mr-2" />
          Scrape Instagram
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-accent" />
            Scrape Instagram for events
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="ig-sources">Sources</Label>
              {pinnedCount > 0 && (
                <span className="text-[10px] uppercase tracking-wider text-cyan-400 flex items-center gap-1">
                  <Star className="w-3 h-3 fill-cyan-400" />
                  {pinnedCount} pinned · prefilled
                </span>
              )}
            </div>
            <Textarea
              id="ig-sources"
              data-testid="scrape-sources-input"
              value={sources}
              onChange={(e) => setSources(e.target.value)}
              placeholder={'@igotskates, @rollerdiscolondon\n#rollerdisco, #skatemeet'}
              disabled={loading}
              rows={6}
              className="font-mono text-sm"
            />
            <div className="flex items-center justify-between gap-3 mt-1.5 text-xs text-muted-foreground">
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <AtSign className="w-3 h-3" /> {handleCount} handle{handleCount !== 1 ? 's' : ''}
                </span>
                <span className="flex items-center gap-1">
                  <Hash className="w-3 h-3" /> {hashtagCount} hashtag{hashtagCount !== 1 ? 's' : ''}
                </span>
              </div>
              {/* Persist edits back to the pinned list so the next manual
                  open AND the next auto-cron both pick up additions. */}
              <Button
                type="button"
                size="sm"
                variant={isDirtyVsPinned ? 'default' : 'ghost'}
                onClick={saveToPinnedList}
                disabled={loading || saving || parsed.length === 0}
                data-testid="scrape-save-pinned-btn"
                className="h-7 text-xs"
              >
                {saving ? (
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                ) : (
                  <Save className="w-3 h-3 mr-1" />
                )}
                {isDirtyVsPinned ? 'Save to pinned list*' : 'Save to pinned list'}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              Mix profiles and hashtags. Each source is scraped separately and runs through AI flyer extraction.
              <strong className="block mt-1 text-foreground/70">Tip:</strong> hit <em>Save to pinned list</em> after editing so you don&apos;t have to retype next time.
            </p>
          </div>
          <div>
            <Label htmlFor="ig-limit">Posts to fetch per source</Label>
            <Input
              id="ig-limit"
              data-testid="scrape-limit-input"
              type="number"
              min="5"
              max="50"
              value={limit}
              onChange={(e) => setLimit(parseInt(e.target.value, 10) || 5)}
              disabled={loading}
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => setOpen(false)}
            disabled={loading}
            data-testid="scrape-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={runScrape}
            disabled={loading || parsed.length === 0}
            data-testid="scrape-run-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Starting…
              </>
            ) : (
              <>
                <Instagram className="w-4 h-4 mr-2" />
                Run scrape
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ScrapeInstagramButton;
